function createRegionWrapper(level) {
  const wrapper = document.createElement("div");
  wrapper.id = `wbAWrap-${level}`;
  return wrapper;
}
function createDuplicateRegions(wrapper, level, regionCount, dictionary) {
  const result = new Array(regionCount).fill(0).map((el, i) => {
    const region = createRegion(level, i, dictionary);
    wrapper.appendChild(region);
    return region;
  });
  return result;
}
function createRegion(level, index, dictionary, role = "log") {
  const region = document.createElement("div");
  region.setAttribute("role", role);
  region.setAttribute("aria-live", level);
  region.classList.add("wbARegion");
  const id = `wbARegion-${level}${index}`;
  region.id = id;
  region.setAttribute("data-testid", id);
  dictionary.set(id, {
    id,
    levelIndex: index,
    level,
    element: region
  });
  return region;
}
function removeMessage(messageElement, removalDelay) {
  setTimeout(() => {
    messageElement.remove();
  }, removalDelay);
}

function alternateIndex(index, count) {
  index += 1;
  index = index % count;
  return index;
}
function createDebounceFunction(context, callback, debounceThreshold) {
  let timeoutId = null;
  const debouncedFn = (...args) => {
    return new Promise(resolve => {
      const later = () => {
        const result = callback.apply(context, args);
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        return resolve(result);
      };
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(() => {
        later();
      }, debounceThreshold);
    });
  };
  debouncedFn.updateWaitTime = newWaitTime => {
    debounceThreshold = newWaitTime;
  };
  return debouncedFn;
}

const REMOVAL_TIMEOUT_DELAY = 5000;
const DEFAULT_WAIT_THRESHOLD = 250;
class Announcer {
  constructor(targetElement = document.body) {
    this.targetElement = void 0;
    this.topLevelId = `wbAnnounce`;
    this.node = null;
    this.regionFactory = {
      count: 2,
      aIndex: 0,
      pIndex: 0
    };
    this.dictionary = new Map();
    this.waitThreshold = DEFAULT_WAIT_THRESHOLD;
    this.lastExecutionTime = 0;
    this.debounced = void 0;
    this.targetElement = targetElement;
    if (typeof document !== "undefined") {
      const announcerCheck = document.getElementById(this.topLevelId);
      if (announcerCheck === null) {
        this.init(this.topLevelId);
      } else {
        this.reattachNodes();
      }
      this.debounced = createDebounceFunction(this, this.processAnnouncement, this.waitThreshold);
    }
  }
  static getInstance(targetElement) {
    if (!Announcer._instance) {
      Announcer._instance = new Announcer(targetElement);
    }
    return Announcer._instance;
  }
  init(id) {
    var _this$node;
    this.node = document.createElement("div");
    this.node.id = id;
    this.node.setAttribute("data-testid", id);
    Object.assign(this.node.style, srOnly);
    const aWrapper = createRegionWrapper("assertive");
    createDuplicateRegions(aWrapper, "assertive", this.regionFactory.count, this.dictionary);
    (_this$node = this.node) == null || _this$node.appendChild(aWrapper);
    const pWrapper = createRegionWrapper("polite");
    createDuplicateRegions(pWrapper, "polite", this.regionFactory.count, this.dictionary);
    this.node.appendChild(pWrapper);
    this.targetElement.append(this.node);
  }
  reattachNodes() {
    const announcerCheck = document.getElementById(this.topLevelId);
    if (announcerCheck !== null) {
      this.node = announcerCheck;
      const regions = Array.from(announcerCheck.querySelectorAll("[id^='wbARegion']"));
      regions.forEach(region => {
        this.dictionary.set(region.id, {
          id: region.id,
          levelIndex: parseInt(region.id.charAt(region.id.length - 1)),
          level: region.getAttribute("aria-live"),
          element: region
        });
      });
    }
  }
  announce(message, level, debounceThreshold) {
    if (debounceThreshold !== undefined) {
      this.updateWaitThreshold(debounceThreshold);
    }
    return this.debounced(this, message, level);
  }
  updateWaitThreshold(debounceThreshold) {
    this.waitThreshold = debounceThreshold;
    if (this.debounced) {
      this.debounced.updateWaitTime(debounceThreshold);
    }
  }
  processAnnouncement(context, message, level) {
    if (!context.node) {
      context.reattachNodes();
    }
    const regions = [...context.dictionary.values()].filter(entry => entry.level === level);
    const newIndex = context.appendMessage(message, level, regions);
    if (level === "assertive") {
      context.regionFactory.aIndex = newIndex;
    } else {
      context.regionFactory.pIndex = newIndex;
    }
    return regions[newIndex].id || "";
  }
  clear(id) {
    if (!this.node) {
      return;
    }
    if (id) {
      var _this$dictionary$get;
      (_this$dictionary$get = this.dictionary.get(id)) == null || _this$dictionary$get.element.replaceChildren();
    } else {
      this.dictionary.forEach(region => {
        region.element.replaceChildren();
      });
    }
  }
  appendMessage(message, level, regionList, debounceThreshold = DEFAULT_WAIT_THRESHOLD) {
    let index = level === "assertive" ? this.regionFactory.aIndex : this.regionFactory.pIndex;
    regionList[index].element.replaceChildren();
    index = alternateIndex(index, this.regionFactory.count);
    const messageEl = document.createElement("p");
    messageEl.textContent = message;
    regionList[index].element.appendChild(messageEl);
    removeMessage(messageEl, debounceThreshold + REMOVAL_TIMEOUT_DELAY);
    return index;
  }
  reset() {
    this.regionFactory.aIndex = 0;
    this.regionFactory.pIndex = 0;
    this.clear();
  }
  destroy() {
    if (this.node) {
      var _this$node$parentElem;
      (_this$node$parentElem = this.node.parentElement) == null || _this$node$parentElem.removeChild(this.node);
    }
    Announcer._instance = null;
  }
}
Announcer._instance = void 0;
const srOnly = {
  border: 0,
  clip: "rect(0,0,0,0)",
  height: 1,
  margin: -1,
  overflow: "hidden",
  padding: 0,
  position: "absolute",
  width: 1
};

function initAnnouncer(props) {
  let targetElement;
  if ((props == null ? void 0 : props.targetElement) !== null) {
    targetElement = props == null ? void 0 : props.targetElement;
  }
  const announcer = Announcer.getInstance(targetElement);
  if ((props == null ? void 0 : props.debounceThreshold) !== undefined) {
    announcer.updateWaitThreshold(props == null ? void 0 : props.debounceThreshold);
  }
  return announcer;
}

function announceMessage({
  message,
  level = "polite",
  debounceThreshold,
  initialTimeout = 150
}) {
  const announcer = Announcer.getInstance();
  if (initialTimeout > 0) {
    return new Promise(resolve => {
      return setTimeout(async () => {
        const result = announcer.announce(message, level, debounceThreshold);
        resolve(result);
      }, initialTimeout);
    });
  } else {
    return announcer.announce(message, level, debounceThreshold);
  }
}

function clearMessages(id) {
  var _document;
  const announcer = Announcer.getInstance();
  if (id && (_document = document) != null && _document.getElementById(id)) {
    announcer.clear(id);
  } else if (typeof document !== "undefined") {
    announcer.clear();
  }
}

export { announceMessage, clearMessages, initAnnouncer };
